/**
 * 強みページ セクションスクロール
 * #strength-items を 100vh に固定し、内部で 01〜05 をフェード切替
 */
const STACKED_LAYOUT_MQ = window.matchMedia('(max-width: 1023px)');
const LOCKED_CLASS = 'is-strength-locked';
const SECTIONS_CLASS = 'is-strength-sections';
const SNAP_TOLERANCE = 16;
const APPROACH_TOLERANCE = 100;
const SECTION_CATCH_EDGE = 48;
const REENTRY_COOLDOWN_MS = 700;
const GUIDE_INTRO_MS = 600;
const TRANSITION_MS = 500;
const WHEEL_THRESHOLD = 8;
const WHEEL_GESTURE_GAP_MS = 200;
const SECTION_SCROLL_LAPSE_MS = Math.max(TRANSITION_MS, 700);

export function strengthFunc() {
  const itemsEl = document.getElementById('strength-items');
  if (!itemsEl) {
    return;
  }

  const items = [...itemsEl.querySelectorAll('.p-strength-item')];
  const pagerCurrent = document.querySelector('.p-strength-pager__current');
  const wheelGuide = document.querySelector('.p-strength-wheel-guide');

  if (items.length === 0) {
    return;
  }

  const lastIndex = items.length - 1;

  let state = 'idle'; // idle | waiting | sections
  let currentIndex = 0;
  let lockedScrollY = 0;
  let suppressTopTrigger = false;
  let suppressBottomTrigger = false;
  let entryEdge = 'top';
  let lastScrollY = window.scrollY;
  let guideIntroTimer = null;
  let scrollHandler = null;
  let wheelHandler = null;
  let approachWheelHandler = null;
  let resizeHandler = null;
  let reentryBlockedUntil = 0;
  let lastScrollDirection = 0;
  let cachedSnapScrollY = null;
  let prevWheelTime = 0;
  let wheelScrollings = [];
  let canScrollSection = true;
  let canScrollTimer = null;
  let panelAnimationFrame = null;

  const padNumber = (value) => String(value).padStart(2, '0');

  const getContainerTop = () => {
    const rect = itemsEl.getBoundingClientRect();
    return Math.max(0, Math.round(rect.top + window.scrollY));
  };

  const updateCachedSnap = () => {
    cachedSnapScrollY = getContainerTop();
  };

  const getBottomExitScrollTop = () => {
    return Math.max(0, getContainerTop() + itemsEl.offsetHeight - window.innerHeight);
  };

  const getSnapScrollY = () => {
    if (state === 'idle') {
      updateCachedSnap();
    }

    return cachedSnapScrollY ?? getContainerTop();
  };

  const snapToStrength = (scrollY = getSnapScrollY()) => {
    window.scrollTo(0, scrollY);
    lastScrollY = scrollY;
  };

  const isContainerSnapped = () => {
    const rect = itemsEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    return (
      Math.abs(rect.top) <= SNAP_TOLERANCE &&
      Math.abs(rect.bottom - viewportHeight) <= SNAP_TOLERANCE
    );
  };

  const isReentryBlocked = () => {
    if (Date.now() >= reentryBlockedUntil) {
      return false;
    }

    const scrollY = window.scrollY;
    const snapScrollY = getSnapScrollY();

    return Math.abs(scrollY - snapScrollY) <= APPROACH_TOLERANCE;
  };

  const blockReentry = () => {
    reentryBlockedUntil = Date.now() + REENTRY_COOLDOWN_MS;
    lastScrollDirection = 0;
  };

  const clearReentryBlock = () => {
    reentryBlockedUntil = 0;
  };

  const getWheelAverage = (elements, number) => {
    const lastElements = elements.slice(Math.max(elements.length - number, 1));
    const sum = lastElements.reduce((total, value) => total + value, 0);

    return Math.ceil(sum / number);
  };

  const resetWheelTracking = () => {
    prevWheelTime = 0;
    wheelScrollings = [];
    canScrollSection = true;

    if (canScrollTimer) {
      window.clearTimeout(canScrollTimer);
      canScrollTimer = null;
    }
  };

  const registerWheelScrolling = (absValue) => {
    const now = Date.now();
    const timeDiff = now - prevWheelTime;
    prevWheelTime = now;

    if (timeDiff > WHEEL_GESTURE_GAP_MS) {
      wheelScrollings = [];
    }

    if (wheelScrollings.length > 149) {
      wheelScrollings.shift();
    }

    wheelScrollings.push(absValue);
  };

  const isWheelAccelerating = () => {
    if (wheelScrollings.length === 0) {
      return false;
    }

    const averageEnd = getWheelAverage(wheelScrollings, 10);
    const averageMiddle = getWheelAverage(wheelScrollings, 70);

    return averageEnd >= averageMiddle;
  };

  const lockSectionScroll = () => {
    canScrollSection = false;

    if (canScrollTimer) {
      window.clearTimeout(canScrollTimer);
    }

    canScrollTimer = window.setTimeout(() => {
      canScrollSection = true;
      canScrollTimer = null;
    }, SECTION_SCROLL_LAPSE_MS);
  };

  const updatePager = (index) => {
    if (pagerCurrent) {
      pagerCurrent.textContent = padNumber(index + 1);
    }
  };

  const setActiveItem = (index, animate = true) => {
    items.forEach((item, itemIndex) => {
      const isActive = itemIndex === index;
      item.classList.toggle('is-active', isActive);

      const inner = item.querySelector('.p-strength-item__inner');
      if (!inner) {
        return;
      }

      if (!animate) {
        inner.classList.toggle('is-active-panel', isActive);
        return;
      }

      inner.classList.remove('is-active-panel');
    });

    if (animate && items[index]) {
      clearPanelAnimationFrame();
      panelAnimationFrame = window.requestAnimationFrame(() => {
        panelAnimationFrame = window.requestAnimationFrame(() => {
          panelAnimationFrame = null;
          items[index].querySelector('.p-strength-item__inner')?.classList.add('is-active-panel');
        });
      });
    }

    updatePager(index);
    currentIndex = index;
  };

  const clearPanelAnimationFrame = () => {
    if (panelAnimationFrame) {
      window.cancelAnimationFrame(panelAnimationFrame);
      panelAnimationFrame = null;
    }
  };

  const showAllPanels = () => {
    items.forEach((item) => {
      item.classList.remove('is-active');
      item.querySelector('.p-strength-item__inner')?.classList.add('is-active-panel');
    });
  };

  const enableStackedLayout = () => {
    clearGuideIntroTimer();
    clearPanelAnimationFrame();
    resetWheelGuide();
    removeScrollHandler();
    removeWheelHandler();
    removeApproachWheelHandler();

    document.documentElement.classList.remove(LOCKED_CLASS, SECTIONS_CLASS);
    unlockScroll();
    showAllPanels();

    state = 'idle';
    entryEdge = 'top';
    cachedSnapScrollY = null;
    suppressTopTrigger = false;
    suppressBottomTrigger = false;
    clearReentryBlock();
    resetWheelTracking();
  };

  const isStackedLayout = () => STACKED_LAYOUT_MQ.matches;

  const resetToFirstPanel = () => {
    setActiveItem(0, false);
  };

  const clearGuideIntroTimer = () => {
    if (guideIntroTimer) {
      window.clearTimeout(guideIntroTimer);
      guideIntroTimer = null;
    }
  };

  const hideWheelGuide = () => {
    clearGuideIntroTimer();

    if (!wheelGuide) {
      return;
    }

    wheelGuide.classList.remove('is-visible', 'is-ready');
    wheelGuide.classList.add('is-hidden');
  };

  const resetWheelGuide = () => {
    clearGuideIntroTimer();

    if (!wheelGuide) {
      return;
    }

    wheelGuide.classList.remove('is-visible', 'is-ready', 'is-hidden');
  };

  const keepWheelGuideActive = () => {
    if (!wheelGuide) {
      return;
    }

    clearGuideIntroTimer();
    wheelGuide.classList.remove('is-hidden', 'is-visible');
    wheelGuide.classList.add('is-ready');
  };

  const showWheelGuide = () => {
    if (!wheelGuide) {
      return;
    }

    resetWheelGuide();
    wheelGuide.classList.add('is-visible');

    guideIntroTimer = window.setTimeout(() => {
      keepWheelGuideActive();
      state = 'sections';
      resetWheelTracking();
      guideIntroTimer = null;
    }, GUIDE_INTRO_MS);
  };

  const lockScroll = () => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  };

  const unlockScroll = () => {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  };

  const removeWheelHandler = () => {
    if (wheelHandler) {
      window.removeEventListener('wheel', wheelHandler, { capture: true });
      wheelHandler = null;
    }
  };

  const removeApproachWheelHandler = () => {
    if (approachWheelHandler) {
      window.removeEventListener('wheel', approachWheelHandler, { capture: true });
      approachWheelHandler = null;
    }
  };

  const removeScrollHandler = () => {
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler);
      scrollHandler = null;
    }
  };

  const teardownLockedMode = () => {
    clearGuideIntroTimer();
    clearPanelAnimationFrame();
    hideWheelGuide();
    removeWheelHandler();

    document.documentElement.classList.remove(LOCKED_CLASS, SECTIONS_CLASS);
    unlockScroll();

    items.forEach((item) => {
      item.classList.remove('is-active');
    });

    state = 'idle';
    entryEdge = 'top';
    cachedSnapScrollY = null;

    resetWheelTracking();
  };

  const goToItem = (index) => {
    if (index === currentIndex) {
      return;
    }

    if (index < 0 || index > lastIndex) {
      return;
    }

    setActiveItem(index, true);
  };

  const exitToTop = (scrollDelta = 0) => {
    const restoreY = lockedScrollY;

    teardownLockedMode();
    suppressTopTrigger = true;
    blockReentry();
    resetToFirstPanel();

    requestAnimationFrame(() => {
      window.scrollTo(0, restoreY);

      if (scrollDelta) {
        window.scrollBy(0, scrollDelta);
      }

      requestAnimationFrame(() => {
        updateCachedSnap();
        setupIdleMode();
      });
    });
  };

  const exitToBottom = (scrollDelta = 0) => {
    const exitIndex = currentIndex;

    teardownLockedMode();
    setActiveItem(exitIndex, false);
    suppressBottomTrigger = true;
    blockReentry();

    requestAnimationFrame(() => {
      const exitY = getBottomExitScrollTop();
      window.scrollTo(0, exitY);

      if (scrollDelta) {
        window.scrollBy(0, scrollDelta);
      }

      requestAnimationFrame(() => {
        updateCachedSnap();
        setupIdleMode();
      });
    });
  };

  const onWheel = (event) => {
    if (state !== 'waiting' && state !== 'sections') {
      return;
    }

    event.preventDefault();

    if (Math.abs(event.deltaY) < WHEEL_THRESHOLD) {
      return;
    }

    const direction = event.deltaY > 0 ? 1 : -1;

    if (state === 'waiting') {
      if (entryEdge === 'top' && direction < 0) {
        exitToTop(event.deltaY);
      }

      if (entryEdge === 'bottom' && direction > 0) {
        exitToBottom(event.deltaY);
      }

      return;
    }

    registerWheelScrolling(Math.abs(event.deltaY));

    if (!canScrollSection || !isWheelAccelerating()) {
      return;
    }

    if (direction < 0 && currentIndex === 0) {
      exitToTop(event.deltaY);
      return;
    }

    if (direction > 0 && currentIndex === lastIndex) {
      exitToBottom(event.deltaY);
      return;
    }

    lockSectionScroll();
    goToItem(currentIndex + direction);
  };

  const addWheelHandler = () => {
    removeWheelHandler();
    wheelHandler = onWheel;
    window.addEventListener('wheel', wheelHandler, { passive: false, capture: true });
  };

  const enterLockedMode = (startIndex = 0) => {
    if (state !== 'idle') {
      return;
    }

    removeScrollHandler();
    removeApproachWheelHandler();
    resetWheelTracking();

    lockedScrollY = getSnapScrollY();
    snapToStrength(lockedScrollY);
    lockScroll();

    document.documentElement.classList.add(LOCKED_CLASS, SECTIONS_CLASS);

    setActiveItem(startIndex, false);
    addWheelHandler();

    entryEdge = startIndex === 0 ? 'top' : 'bottom';
    showWheelGuide();
    state = 'waiting';
  };

  const tryEnterFromTop = () => {
    if (suppressTopTrigger || isReentryBlocked()) {
      return false;
    }

    enterLockedMode(0);
    return true;
  };

  const tryEnterFromBottom = () => {
    if (suppressBottomTrigger || isReentryBlocked()) {
      return false;
    }

    enterLockedMode(lastIndex);
    return true;
  };

  const onApproachWheel = (event) => {
    if (state !== 'idle' || isReentryBlocked()) {
      return;
    }

    if (Math.abs(event.deltaY) < WHEEL_THRESHOLD) {
      return;
    }

    const rect = itemsEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const direction = event.deltaY > 0 ? 1 : -1;

    if (
      direction > 0 &&
      !suppressTopTrigger &&
      rect.top <= SECTION_CATCH_EDGE &&
      rect.top > -SNAP_TOLERANCE
    ) {
      event.preventDefault();
      tryEnterFromTop();
      return;
    }

    if (
      direction < 0 &&
      !suppressBottomTrigger &&
      rect.bottom >= viewportHeight - SECTION_CATCH_EDGE &&
      rect.bottom <= viewportHeight + SNAP_TOLERANCE
    ) {
      event.preventDefault();
      tryEnterFromBottom();
    }
  };

  const addApproachWheelHandler = () => {
    removeApproachWheelHandler();
    approachWheelHandler = onApproachWheel;
    window.addEventListener('wheel', approachWheelHandler, { passive: false, capture: true });
  };

  const onApproachScroll = () => {
    if (state !== 'idle') {
      return;
    }

    const scrollY = window.scrollY;
    const snapScrollY = getSnapScrollY();
    const prevScrollY = lastScrollY;
    const scrollingUp = scrollY < prevScrollY - 1;
    const scrollingDown = scrollY > prevScrollY + 1;

    if (scrollingDown) {
      lastScrollDirection = 1;
    } else if (scrollingUp) {
      lastScrollDirection = -1;
    }

    const hitSnapFromAbove =
      scrollingDown && prevScrollY < snapScrollY && scrollY >= snapScrollY;
    const hitSnapFromBelow =
      scrollingUp && prevScrollY > snapScrollY && scrollY <= snapScrollY;
    const passedThroughFromAbove =
      scrollingDown && prevScrollY < snapScrollY && scrollY > snapScrollY;
    const passedThroughFromBelow =
      scrollingUp && prevScrollY > snapScrollY && scrollY < snapScrollY;
    lastScrollY = scrollY;

    if (suppressTopTrigger && scrollY < snapScrollY - APPROACH_TOLERANCE) {
      suppressTopTrigger = false;
      clearReentryBlock();
    }

    if (suppressBottomTrigger && scrollY > snapScrollY + APPROACH_TOLERANCE) {
      suppressBottomTrigger = false;
      clearReentryBlock();
    }

    if (isReentryBlocked()) {
      return;
    }

    const snapped = isContainerSnapped();

    if (
      scrollingDown &&
      (hitSnapFromAbove || passedThroughFromAbove || (snapped && prevScrollY < snapScrollY))
    ) {
      tryEnterFromTop();
      return;
    }

    if (
      scrollingUp &&
      (hitSnapFromBelow || passedThroughFromBelow || (snapped && prevScrollY > snapScrollY))
    ) {
      tryEnterFromBottom();
    }
  };

  const setupIdleMode = () => {
    removeScrollHandler();
    removeWheelHandler();
    removeApproachWheelHandler();
    updateCachedSnap();
    lastScrollY = window.scrollY;

    scrollHandler = onApproachScroll;
    window.addEventListener('scroll', scrollHandler, { passive: true });
    addApproachWheelHandler();
  };

  const teardownAll = () => {
    enableStackedLayout();
  };

  const handleBreakpoint = (event) => {
    if (event.matches) {
      enableStackedLayout();
      return;
    }

    resetToFirstPanel();
    updateCachedSnap();
    setupIdleMode();
  };

  resizeHandler = () => {
    if (isStackedLayout()) {
      if (
        state !== 'idle' ||
        document.documentElement.classList.contains(LOCKED_CLASS) ||
        document.documentElement.classList.contains(SECTIONS_CLASS)
      ) {
        enableStackedLayout();
      }

      return;
    }

    updateCachedSnap();

    if (state !== 'idle') {
      window.scrollTo(0, lockedScrollY);
    }
  };

  if (isStackedLayout()) {
    enableStackedLayout();
  } else {
    updateCachedSnap();
    resetToFirstPanel();
    setupIdleMode();
  }

  window.addEventListener('resize', resizeHandler);

  if (typeof STACKED_LAYOUT_MQ.addEventListener === 'function') {
    STACKED_LAYOUT_MQ.addEventListener('change', handleBreakpoint);
  } else {
    STACKED_LAYOUT_MQ.addListener(handleBreakpoint);
  }
}
